'use client';

import { DateTime } from 'luxon';
import React, { useEffect, useRef, useState } from 'react';
import CloseIcon from '@/assets/icons/close.svg';
import FilterIcon from '@/assets/icons/filter.svg';
import FilterIconBlack from '@/assets/icons/filterIconBlack.svg';
import ListViewSVG from '@/assets/icons/list-view.svg';
import TileViewSVG from '@/assets/icons/tile-view.svg';
import { isBrowser } from '../../../helpers/is-browser';
import {
  ListCard,
  ListType,
} from '../../../helpers/list-page/interfaces/list-card.interface';
import {
  _pageSize,
  _searchDelay,
} from '../../../helpers/list-page/list-page-constants';
import { calculateActiveFilters } from '../../../helpers/list-page/list-page-helpers';
import {
  cardIsOffer,
  cardIsTenant,
} from '../../../helpers/list-page/type-guards';
import { normalizeTextString } from '../../../helpers/normalizeTextString';

import { Breadcrumbs } from '../../breadcrumbs';
import { RichText } from '../../rich-text/rich-text';
import Backdrop from '../../ui/backdrop/backdrop';
import Button from '../../ui/button/button';
import InputField from '../../ui/input-field/input-field';
import { SideModal } from '../../ui/side-modal/side-modal';
import { SideModalBody } from '../../ui/side-modal/side-modal-body';
import FilterSidebar from './filter-sidebar';
import { ListView } from './list-view';
import { NoHits } from './no-hits';
import ShowMore from './show-more';
import { TileView } from './tile-view';
import { PageContext } from '@/templates/page-context.interface';
import {
  defaultAttributeFilters,
  ILibraryTenantAttributeFilters,
} from '@/lib/fragments/libraryTenantAttributeFilters';
import {
  SelectOption,
  SelectValue,
} from '@/components/ui/select/select.interface';
import { useDebounce } from '@/hooks/use-debounce';
import {
  isDateTimeInFuture,
  isDateTimeInPast,
  isDateTimeInRange,
} from '@/helpers/dates';
import Select from '@/components/ui/select/select';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { IGtmParameters } from '@/hooks/use-send-gtm-event';
import {
  useGetLibraryConstantsDictionaryData,
  useIsMPLivliFamily,
} from '@/providers/ConfigProvider';
import { useUser } from '@/providers/UserProvider';
import { isMemberDayTag } from '@/components/constants/memberdayTagName';
import { sortByPriority } from '@/helpers/utils';

interface ListPageProps {
  cardList: ListCard[];
  pageName: string;
  pageSlug: string;
  node_locale: string;
  // location: any;
  homeText: string;
  breadcrumbs: string[];
  tileColClass: string;
  listType: ListType;
  attributeFilters?: ILibraryTenantAttributeFilters;
  pageDescription?: any;
  isWebView?: boolean;
  pageContext?: PageContext;
  businessUnitCode?: string;
  config?: any;
  initialPage?: number;
  ikeaFamilyBenefit?:
    | import('@/lib/getIkeaFamilyBenefits').IkeaFamilyBenefitItem
    | null;
}

const ListPage = ({
  cardList,
  pageName,
  pageSlug,
  node_locale,
  // location,
  homeText,
  breadcrumbs,
  tileColClass,
  listType,
  attributeFilters,
  pageDescription,
  isWebView,
  pageContext,
  businessUnitCode,
  config,
  initialPage = 1,
  ikeaFamilyBenefit,
}: ListPageProps) => {
  const isLivliFamily = useIsMPLivliFamily();
  const { user } = useUser();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [showAll, setShowAll] = useState({
    category: false,
    cuisine: false,
    tag: false,
  });
  const [activeFilterString, setActiveFilterString] = useState<string | null>(
    null,
  );
  const [scrollPositionOnFilter, setScrollPositionOnFilter] =
    useState<number>(0);
  const defaultSelectedOption: SelectOption = {
    optionText: '',
    optionValue: '',
  };
  const [activeFilterCategory, setActiveFilterCategory] =
    useState<SelectOption>(defaultSelectedOption);

  const [activeFilterType, setActiveFilterType] = useState<SelectOption>(
    defaultSelectedOption,
  );

  const [activeFilterPriceLevel, setActiveFilterPriceLevel] =
    useState<SelectOption>(defaultSelectedOption);

  //offers
  const [offerCard, setOfferCard] = useState<boolean>(() => {
    const inurl = searchParams.get('offerCard');
    if (inurl) {
      return true;
    } else {
      return false;
    }
  });
  // Gift card
  const [giftCard, setGiftCard] = useState<boolean>(() => {
    const inurl = searchParams.get('giftcard');
    if (inurl) {
      return true;
    } else {
      return false;
    }
  });
  // Loyalty card
  const [loyaltyCard, setLoyaltyCard] = useState<boolean>(() => {
    const inurl = searchParams.get('loyaltycard');
    if (inurl) {
      return true;
    } else {
      return false;
    }
  });

  // tags
  const [activeFilterTag, setActiveFilterTag] = useState<SelectOption>(
    defaultSelectedOption,
  );

  // member dat tag
  const [activeMemberDayCard, setActiveMemberDayCard] = useState<boolean>(
    () => {
      const inurl = searchParams.get('memberday');
      if (inurl) {
        return true;
      } else {
        return false;
      }
    },
  );

  const handleGiftCardChange = () => {
    setGiftCard(!giftCard);
    setLoyaltyCard(false);
    setOfferCard(false);
    setActiveMemberDayCard(false);
  };

  const handleLoyaltyCardChange = () => {
    setLoyaltyCard(!loyaltyCard);
    setGiftCard(false);
    setOfferCard(false);
    setActiveMemberDayCard(false);
  };

  const handleOfferCardChange = () => {
    setOfferCard(!offerCard);
    setGiftCard(false);
    setLoyaltyCard(false);
    setActiveMemberDayCard(false);
  };

  const handleMemberDayCardChange = () => {
    setActiveMemberDayCard(!activeMemberDayCard);
    setOfferCard(false);
    setGiftCard(false);
    setLoyaltyCard(false);
  };

  const debouncedFilterString = useDebounce<string>(
    activeFilterString ?? '',
    _searchDelay,
  );

  const debouncedGtmFilterString = useDebounce<string>(
    activeFilterString ?? '',
    _searchDelay * 3, // 3 times longer delay for GTM events to reduce the number of events sent with partial search strings
  );

  const debouncedCategoryString = useDebounce<string>(
    activeFilterCategory?.optionValue ?? '',
    _searchDelay,
  );

  const debouncedTypeString = useDebounce<string>(
    activeFilterType?.optionValue ?? '',
    _searchDelay,
  );

  const debouncedPriceLevelString = useDebounce<string>(
    activeFilterPriceLevel?.optionValue ?? '',
    _searchDelay,
  );

  const debouncedOfferCard = useDebounce<boolean>(
    offerCard ?? false,
    _searchDelay,
  );

  const debouncedGiftCard = useDebounce<boolean>(
    giftCard ?? false,
    _searchDelay,
  );

  const debouncedLoyaltyCard = useDebounce<boolean>(
    loyaltyCard ?? false,
    _searchDelay,
  );

  const debouncedMemberDayCard = useDebounce<boolean>(
    activeMemberDayCard ?? false,
    _searchDelay,
  );

  const debouncedTag = useDebounce<string>(
    activeFilterTag?.optionValue ?? '',
    _searchDelay,
  );

  const filterFunctionQuery = (card: ListCard) => {
    if (!debouncedFilterString) return true;

    const matchesName = normalizeTextString(card.name).includes(
      normalizeTextString(debouncedFilterString),
    );
    if (cardIsOffer(card)) {
      const matchesBrand = card?.brand
        ? normalizeTextString(card.brand).includes(
            normalizeTextString(debouncedFilterString),
          )
        : false;
      return matchesName || matchesBrand;
    }
    return matchesName;
  };

  const filterFunctionCategory = (card: ListCard) => {
    if (!debouncedCategoryString) return true;

    return card?.categories
      ?.map((c) => normalizeTextString(c.name))
      ?.includes(normalizeTextString(debouncedCategoryString));
  };

  const filterFunctionType = (card: ListCard) => {
    if (!debouncedTypeString) return true;
    if (cardIsTenant(card)) {
      return card?.cuisineTypes
        ?.map((c) => normalizeTextString(c.name))
        ?.includes(normalizeTextString(debouncedTypeString));
    }
  };

  const filterFunctionPriceLevel = (card: ListCard) => {
    if (!debouncedPriceLevelString) return true;
    if (cardIsTenant(card) && card?.priceLevel) {
      return normalizeTextString(card?.priceLevel?.name)?.includes(
        normalizeTextString(debouncedPriceLevelString),
      );
    }
  };

  const filterAttributeOffers = (card: ListCard) => {
    if (!debouncedOfferCard) return true;
    if (cardIsTenant(card) && debouncedOfferCard) {
      const cardHasOffercard = card?.offerPage ?? false;

      return cardHasOffercard === offerCard;
    }
    return true;
  };

  const filterAttributeGiftCard = (card: ListCard) => {
    if (cardIsTenant(card) && debouncedGiftCard) {
      const cardHasGiftcard = card?.giftCard ?? false;

      return cardHasGiftcard === giftCard;
    }
    return true;
  };
  const filterAttributeLoyaltyCard = (card: ListCard) => {
    if (cardIsTenant(card) && debouncedLoyaltyCard) {
      const cardHasLoyaltyCard = card?.loyaltyCard ?? false;

      return cardHasLoyaltyCard === loyaltyCard;
    }
    return true;
  };
  const filterAttributeMemberDayCard = (card: ListCard) => {
    const isLivliFamily = config?.NEXT_PUBLIC_IS_MP_LIVLI_FAMILY === 'true';

    if (isLivliFamily && cardIsTenant(card) && debouncedMemberDayCard) {
      const cardHasMemberDayCard =
        (card?.offerCampaignNames ?? [])?.filter((tag) => isMemberDayTag(tag))
          ?.length > 0;

      return cardHasMemberDayCard === activeMemberDayCard;
    }
    return true;
  };

  const filterTags = (card: ListCard) => {
    if (!debouncedTag) return true;

    if (cardIsOffer(card)) {
      return card?.tags
        ?.map((tag) => normalizeTextString(tag?.tagName, true))
        ?.includes(normalizeTextString(debouncedTag, true));
    }
    return true;
  };

  const [filteredCardList, setFilteredCardList] = useState<ListCard[]>(
    cardList
      ?.filter(filterFunctionQuery)
      ?.filter(filterFunctionCategory)
      ?.filter(filterFunctionType)
      ?.filter(filterFunctionPriceLevel)
      ?.filter(filterAttributeGiftCard)
      ?.filter(filterAttributeOffers)
      ?.filter(filterAttributeLoyaltyCard)
      ?.filter(filterTags)
      ?.filter(filterAttributeMemberDayCard),
  );
  const isMemberDayCard = (card: ListCard) => {
    const isLivliFamily = config?.NEXT_PUBLIC_IS_MP_LIVLI_FAMILY === 'true';
    if (isLivliFamily && cardIsTenant(card)) {
      const cardHasMemberDayCard =
        (card?.offerCampaignNames ?? [])?.filter((tag) => isMemberDayTag(tag))
          ?.length > 0;

      return cardHasMemberDayCard;
    }
    return false;
  };

  const atLeastOneOfTheCardsHasEitherGiftcardOrLoyaltycard = cardList?.some(
    (card) =>
      (cardIsTenant(card) &&
        ((card?.giftCard && attributeFilters?.giftCardIcon) ||
          (card?.loyaltyCard && attributeFilters?.loyaltyCardIcon) ||
          card?.offerPage)) ||
      isMemberDayCard(card),
  );

  const offerExistsInCard = cardList?.some(
    (card) => cardIsTenant(card) && card?.offerPage,
  );

  const memberDayExistsInCard = cardList?.some(
    (card) => cardIsTenant(card) && isMemberDayCard(card),
  );

  const [activeMode, setActiveMode] = useState<'list' | 'tile'>('tile');
  // const [page, setPage] = useState(1);
  const [page, setPage] = useState(() => {
    if (initialPage && initialPage > 1) return initialPage;
    if (isBrowser) {
      const pageParam = searchParams.get('page');
      return pageParam ? parseInt(pageParam, 10) : 1;
    }
    return 1;
  });

  const isInitialMount = useRef(true);

  const [isMobileSearchFilterOpen, setIsMobileSearchFilterOpen] =
    useState<boolean>(false);
  const [activeFiltersCount, setActiveFiltersCount] = useState<number>(
    calculateActiveFilters(
      isMobileSearchFilterOpen ? activeFilterString : null,
      activeFilterCategory?.optionValue ?? '',
      activeFilterType?.optionValue ?? '',
      activeFilterPriceLevel?.optionValue ?? '',
      giftCard,
      loyaltyCard,
      offerCard,
      activeFilterTag?.optionValue ?? '',
      activeMemberDayCard,
    ),
  );

  const handleOpenMobileSearchFilter = () => {
    setIsMobileSearchFilterOpen(!isMobileSearchFilterOpen);
  };

  const [openMobileCategoryList, setOpenMobileCategoryList] =
    useState<boolean>(false);

  const [openFilterPopup, setOpenFilterPopup] = useState<boolean>(false);

  const resetFilters = () => {
    setActiveFilterString(null);
    setActiveFilterCategory(defaultSelectedOption);
    setActiveFilterType(defaultSelectedOption);
    setActiveFilterPriceLevel(defaultSelectedOption);
    setOfferCard(false);
    setIsMobileSearchFilterOpen(false);
    setFilteredCardList([...cardList]);
    setGiftCard(false);
    setLoyaltyCard(false);
    setActiveFilterTag(defaultSelectedOption);
    setActiveMemberDayCard(false);
  };
  const resetCategory = () => {
    setActiveFilterCategory(defaultSelectedOption);
    setIsMobileSearchFilterOpen(false);
  };

  const changeFilter = () => {
    setOpenFilterPopup(true);
  };

  const selectValueCategories: SelectValue[] = [];
  cardList?.forEach((card) =>
    card.categories?.forEach((cardCategory) => {
      if (
        selectValueCategories.filter(
          (selectValueCategory) =>
            selectValueCategory.value?.optionValue?.toLocaleLowerCase() ===
            cardCategory?.name?.toLocaleLowerCase(),
        ).length === 0 &&
        cardCategory?.name != ''
      ) {
        selectValueCategories.push({
          value: {
            optionText: cardCategory?.name,
            optionValue: cardCategory?.name,
          },
          id: cardCategory?.id,
        });
      }
    }),
  );

  selectValueCategories.sort((a, b) =>
    a.value?.optionValue
      ?.toLocaleLowerCase()
      .localeCompare(b.value?.optionValue?.toLocaleLowerCase()),
  );

  const selectValueTypes: SelectValue[] = [];
  cardList?.forEach((card) => {
    if (cardIsTenant(card)) {
      card.cuisineTypes?.forEach((cardType) => {
        if (
          selectValueTypes.filter(
            (selectValueType) =>
              selectValueType.value?.optionValue?.toLocaleLowerCase() ===
              cardType?.name?.toLocaleLowerCase(),
          ).length === 0 &&
          cardType?.name != ''
        ) {
          selectValueTypes.push({
            value: {
              optionText: cardType?.name,
              optionValue: cardType?.name,
            },
            id: cardType?.id,
          });
        }
      });
    }
  });

  selectValueTypes.sort((a, b) =>
    a.value?.optionValue
      ?.toLocaleLowerCase()
      .localeCompare(b.value?.optionValue?.toLocaleLowerCase()),
  );

  const priceLevelValues: SelectValue[] = [];
  cardList?.forEach((card) => {
    if (cardIsTenant(card) && card?.priceLevel && card?.priceLevel?.name) {
      if (
        priceLevelValues.filter(
          (selectValueType) =>
            selectValueType.value?.optionValue?.toLocaleLowerCase() ===
            card?.priceLevel?.name?.toLocaleLowerCase(),
        ).length === 0
      ) {
        priceLevelValues.push({
          value: {
            optionText: card?.priceLevel?.name,
            optionValue: card?.priceLevel?.name,
          },
          id: card?.priceLevel?.id,
        });
      }
    }
  });

  priceLevelValues.sort((a, b) =>
    a.value?.optionValue
      ?.toLocaleLowerCase()
      .localeCompare(b.value?.optionValue?.toLocaleLowerCase()),
  );

  const tagValues: SelectValue[] = [];
  cardList?.forEach((card) => {
    if (cardIsOffer(card)) {
      card.tags?.forEach((cardTag) => {
        const tagName = cardTag?.tagName;
        if (
          tagValues.filter(
            (selectTag) =>
              selectTag?.value?.optionValue?.toLocaleLowerCase()?.trim() ===
              tagName?.toLocaleLowerCase()?.trim(),
          ).length === 0 &&
          tagName &&
          tagName != ''
        ) {
          tagValues.push({
            value: {
              optionText: tagName,
              optionValue: tagName,
            },
            id: tagName,
          });
        }
      });
    }
  });

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      setPage(1);
    }
    setFilteredCardList(() =>
      cardList
        ?.filter(filterFunctionQuery)
        ?.filter(filterFunctionCategory)
        ?.filter(filterFunctionType)
        ?.filter(filterFunctionPriceLevel)
        ?.filter(filterAttributeOffers)
        ?.filter(filterAttributeGiftCard)
        ?.filter(filterAttributeLoyaltyCard)
        ?.filter(filterTags)
        ?.filter(filterAttributeMemberDayCard),
    );
    setActiveFiltersCount(
      calculateActiveFilters(
        isMobileSearchFilterOpen ? debouncedFilterString : null,
        debouncedCategoryString,
        debouncedTypeString,
        debouncedPriceLevelString,
        debouncedGiftCard,
        debouncedLoyaltyCard,
        debouncedOfferCard,
        debouncedTag,
        debouncedMemberDayCard,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedCategoryString,
    debouncedTypeString,
    debouncedFilterString,
    debouncedGiftCard,
    debouncedLoyaltyCard,
    debouncedOfferCard,
    debouncedPriceLevelString,
    debouncedTag,
    debouncedMemberDayCard,
    node_locale,
    pageSlug,
    debouncedGtmFilterString,
  ]);

  useEffect(() => {
    sendGtmEvents(
      debouncedCategoryString,
      debouncedGtmFilterString,
      debouncedTypeString,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCategoryString, debouncedGtmFilterString, debouncedTypeString]);

  const sendGtmEvents = (
    category: string,
    filter: string,
    cuisineType: string,
  ) => {
    if (
      (category || filter || cuisineType) &&
      (listType === 'shop' ||
        listType === 'entertainment' ||
        listType === 'food_and_beverage' ||
        listType === 'service' ||
        listType === 'offer' ||
        listType === 'news' ||
        listType === 'event' ||
        listType === 'editorial' ||
        listType === 'leisure_package_offer')
    ) {
      const gtmEventName = `${listType}_search`;
      const gtmParams: IGtmParameters = {
        event: gtmEventName,
        search_term: filter ? filter : 'No search query. Filter used',
        search_category: category.length > 0 ? category : 'all',
        search_cuisine_type: cuisineType.length > 0 ? cuisineType : 'all',
        user_status: isLivliFamily ? (user ? 'logged_in' : 'guest') : undefined,
      };

      dataLayer.push(gtmParams);
    }
  };

  useEffect(() => {
    const handleUrl = () => {
      setScrollPositionOnFilter(window.scrollY);

      const params = new URLSearchParams(searchParams.toString());
      const paramsBefore = params.toString();

      if (debouncedCategoryString) {
        params.set('category', debouncedCategoryString);
      } else {
        params.delete('category');
      }
      if (debouncedTypeString) {
        params.set('cuisinetype', debouncedTypeString);
      } else {
        params.delete('cuisinetype');
      }
      if (debouncedPriceLevelString) {
        params.set('priceLevel', debouncedPriceLevelString);
      } else {
        params.delete('priceLevel');
      }
      if (debouncedFilterString) {
        params.set('search', debouncedFilterString);
      } else {
        params.delete('search');
      }
      if (debouncedGiftCard) {
        params.set('giftcard', debouncedGiftCard.toString());
      } else {
        params.delete('giftcard');
      }
      if (debouncedLoyaltyCard) {
        params.set('loyaltycard', debouncedLoyaltyCard.toString());
      } else {
        params.delete('loyaltycard');
      }
      if (debouncedOfferCard) {
        params.set('offers', debouncedOfferCard.toString());
      } else {
        params.delete('offers');
      }
      if (debouncedMemberDayCard) {
        params.set('memberDay', debouncedMemberDayCard.toString());
      } else {
        params.delete('memberDay');
      }
      if (debouncedTag) {
        params.set('tag', debouncedTag);
      } else {
        params.delete('tag');
      }
      if (page > 1) {
        params.set('page', page.toString());
      } else {
        params.delete('page');
      }

      const paramsAfter = params.toString();

      const searchString =
        paramsAfter.length > 0 ? `?${paramsAfter}` : pathname;
      if (paramsBefore !== paramsAfter) {
        console.log('search string', searchString);
        router.push(searchString, { scroll: false });
        setTimeout(() => {
          window.scrollTo(0, scrollPositionOnFilter);
        }, _searchDelay);
      }
    };
    handleUrl();
    // location.href should NOT be a dependency below even if VS Code says so, it triggers the re-rendering twice /samuel
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedFilterString,
    debouncedCategoryString,
    debouncedTypeString,
    debouncedGiftCard,
    debouncedMemberDayCard,
    debouncedPriceLevelString,
    debouncedLoyaltyCard,
    debouncedOfferCard,
    debouncedTag,
    node_locale,
    pageSlug,
    page,
  ]);

  useEffect(() => {
    if (isBrowser && (pathname.length > 0 || searchParams)) {
      const category = searchParams.get('category');
      const cuisinetype = searchParams.get('cuisinetype');
      const priceLevel = searchParams.get('priceLevel');
      const search = searchParams.get('search');
      const pageParam = searchParams.get('page');
      const tag = searchParams.get('tag');
      if (category) {
        setActiveFilterCategory({
          optionText: category,
          optionValue: category,
        });
      }
      if (cuisinetype) {
        setActiveFilterType({
          optionText: cuisinetype,
          optionValue: cuisinetype,
        });
      }
      if (priceLevel) {
        setActiveFilterPriceLevel({
          optionText: priceLevel,
          optionValue: priceLevel,
        });
      }
      if (search) {
        setActiveFilterString(search);
      }
      if (pageParam) {
        const parsedPage = parseInt(pageParam, 10);
        if (!isNaN(parsedPage) && parsedPage > 0) {
          setPage(parsedPage);
        }
      }
      if (tag) {
        setActiveFilterTag({
          optionText: tag,
          optionValue: tag,
        });
      }
    }
  }, []);

  useEffect(() => {
    isMobileSearchFilterOpen
      ? document.body.classList.add('backdrop')
      : document.body.classList.remove('backdrop');
  }, [isMobileSearchFilterOpen]);

  // Sort hits depending on type. Date order for news and event, A-Z for others.
  let slicedSortedList;
  if (listType === 'event') {
    const upcomingEvents = filteredCardList
      ?.filter((event: any) =>
        isDateTimeInFuture(event.startDate, event.timezone),
      )
      ?.sort(
        (a: any, b: any) =>
          DateTime.fromISO(a.startDate).toMillis() -
          DateTime.fromISO(b.startDate).toMillis(),
      );

    const recurringEvents = filteredCardList
      ?.filter((event: any) =>
        isDateTimeInRange(event.startDate, event.endDate, event.timezone),
      )
      ?.sort(
        (a: any, b: any) =>
          DateTime.fromISO(a.startDate).toMillis() -
          DateTime.fromISO(b.startDate).toMillis(),
      );

    const pastEvents = filteredCardList
      ?.filter((event: any) => isDateTimeInPast(event.endDate, event.timezone))
      ?.sort(
        (a: any, b: any) =>
          DateTime.fromISO(b.endDate).toMillis() -
          DateTime.fromISO(a.endDate).toMillis(),
      );

    // Apply priority sorting to upcoming and recurring events
    const upcomingSortedByPriority = upcomingEvents?.sort(sortByPriority);
    const recurringSortedByPriority = recurringEvents?.sort(sortByPriority);

    slicedSortedList = [
      ...(upcomingSortedByPriority || []),
      ...(recurringSortedByPriority || []),
      ...(pastEvents || []),
    ].slice(0, _pageSize * page);
  } else if (listType === 'offer') {
    slicedSortedList = filteredCardList
      ?.sort(sortByPriority)
      .slice(0, _pageSize * page);
  } else if (listType === 'news') {
    slicedSortedList = filteredCardList
      ?.sort((a: any, b: any) => b.date - a.date)
      .slice(0, _pageSize * page);
  } else if (listType === 'occasion') {
    slicedSortedList = filteredCardList
      ?.sort(
        (a, b) =>
          new Date(b.updatedAt as Date).getTime() -
          new Date(a.updatedAt as Date).getTime(),
      )
      .slice(0, _pageSize * page);
  } else if (listType === 'leisure_package_offer') {
    slicedSortedList = filteredCardList
      ?.sort((a: any, b: any) => b.date - a.date)
      .slice(0, _pageSize * page);
  } else {
    slicedSortedList = filteredCardList
      ?.sort((a: any, b: any) => {
        return a.name
          ?.trim()
          .toLocaleLowerCase()
          .localeCompare(b.name?.trim().toLocaleLowerCase());
      })
      .slice(0, _pageSize * page);
  }

  const shouldShowListTypeIcons =
    listType === 'shop' ||
    listType === 'entertainment' ||
    listType === 'food_and_beverage';

  // hide Search and Filter panel for editorial
  const shouldShowSearchAndFilterPanel = ![
    'editorial',
    'leisure_package_offer',
  ].includes(listType);
  const shouldShowTypeFilter = listType === 'food_and_beverage';

  const dictionary = useGetLibraryConstantsDictionaryData();

  if (attributeFilters) {
    attributeFilters.offerPageAvailable = offerExistsInCard;
    attributeFilters.offerPageLabel = dictionary?.offers ?? 'offers';
    attributeFilters.memberDayCardAvailable = memberDayExistsInCard;
    attributeFilters.memberDayCardLabel = dictionary?.memberDayText;
  } else {
    attributeFilters = defaultAttributeFilters;
    attributeFilters.offerPageAvailable = offerExistsInCard;
    attributeFilters.offerPageLabel = dictionary?.offers ?? 'offers';
    attributeFilters.memberDayCardAvailable = memberDayExistsInCard;
    attributeFilters.memberDayCardLabel = dictionary?.memberDayText;
  }
  return (
    <main className="px-6 lg:px-0">
      <Breadcrumbs
        breadcrumbs={breadcrumbs}
        homeText={homeText}
        cssClass={isWebView ? '!pt-8' : ''}
        node_locale={node_locale}
      />
      <div
        className={`flex items-center justify-between mt-2 ${pageDescription ? 'mb-2' : 'mb-6'}`}
      >
        <h1 className="font-title text-headingm text-text-default">
          {pageName}
        </h1>
        {/* Mobile inline filter button */}
        {!isMobileSearchFilterOpen && shouldShowSearchAndFilterPanel && (
          <button
            className="lg:hidden relative flex-shrink-0 grid h-12 w-12 place-items-center rounded-full bg-neutral-grey-100 outline-0 transition-all lg:hover:bg-neutral-grey-200 active:bg-neutral-grey-200"
            onClick={() => setIsMobileSearchFilterOpen(true)}
          >
            {activeFiltersCount > 0 && (
              <div className="absolute -right-1 -top-0.5 lg:hidden grid h-5 w-5 place-items-center rounded-full bg-brand-red-2 font-title text-buttonlabelxs text-brand-white">
                <span>{activeFiltersCount}</span>
              </div>
            )}
            <FilterIcon aria-label="Show filters" />
          </button>
        )}
      </div>
      {pageDescription && (
        <div className="mb-10 text-neutral-grey-700">
          <RichText richText={pageDescription} />
        </div>
      )}

      {shouldShowSearchAndFilterPanel && (
        <div className="mt-4 rounded-xl bg-background-primary-light bg-dynamicStrokeGrey bg-cover bg-top bg-no-repeat px-10 py-8 max-lg:hidden">
          <div className="flex items-center">
            <div className="hidden w-10/12 items-center justify-start lg:flex">
              <InputField
                placeholder={`${
                  dictionary?.find
                } ${pageName?.toLocaleLowerCase()}`}
                label=""
                disabled={false}
                type="text"
                name=""
                id=""
                value={activeFilterString ?? ''}
                onChange={(e) => setActiveFilterString(e.currentTarget.value)}
                // onChange={() => {}}
                icon="Search"
                className="relative mr-5 min-w-[20rem] max-w-[25rem] flex-1"
              />
              <>
                <Select
                  onChange={setActiveFilterCategory}
                  values={selectValueCategories}
                  label=""
                  disabled={false}
                  value={activeFilterCategory.optionValue}
                  defaultValue={dictionary?.all ?? ''}
                  className={`flex-1 border-neutral-grey-200 ${
                    shouldShowTypeFilter ? 'max-w-[250px]' : ''
                  }`}
                  type="primary"
                  setOpenList={setOpenMobileCategoryList}
                  openList={openMobileCategoryList}
                  searchAll={resetCategory}
                />
              </>
              {(selectValueCategories?.length > 0 ||
                selectValueTypes?.length > 0 ||
                priceLevelValues?.length > 0 ||
                tagValues?.length > 0 ||
                atLeastOneOfTheCardsHasEitherGiftcardOrLoyaltycard) && (
                <>
                  <Button
                    variant="secondary"
                    size="regular"
                    className="ml-5 flex items-center"
                    onClick={changeFilter}
                  >
                    <FilterIconBlack
                      aria-label="Show filters"
                      className="mr-1.5"
                    />
                    {dictionary?.allFilters}
                  </Button>
                </>
              )}
              {activeFiltersCount > 0 && (
                <div className="relative -top-4 right-3.5 grid h-5 w-5 place-items-center rounded-full bg-brand-red-2 font-title text-buttonlabelxs text-brand-white">
                  <span>{activeFiltersCount}</span>
                </div>
              )}
            </div>

            <div className="flex lg:hidden">
              {(selectValueCategories?.length > 0 ||
                selectValueTypes?.length > 0 ||
                priceLevelValues?.length > 0 ||
                tagValues?.length > 0 ||
                atLeastOneOfTheCardsHasEitherGiftcardOrLoyaltycard) && (
                <>
                  <Button
                    onClick={handleOpenMobileSearchFilter}
                    variant="secondary"
                    className="flex items-center"
                  >
                    <FilterIconBlack
                      aria-label="Show filters"
                      className="mr-1.5"
                    />
                    {dictionary?.allFilters}
                  </Button>
                </>
              )}
              {activeFiltersCount > 0 && (
                <div className="relative right-3.5 top-4 grid h-5 w-5 place-items-center rounded-full bg-brand-red-2 font-title text-buttonlabelxs text-brand-white">
                  <span>{activeFiltersCount}</span>
                </div>
              )}
            </div>

            {shouldShowListTypeIcons && (
              <div className="ml-auto flex">
                <button
                  type="button"
                  onClick={() => setActiveMode('tile')}
                  disabled={activeMode === 'tile'}
                  className={
                    'flex h-12 w-12 items-center justify-center rounded-full outline-none lg:enabled:hover:bg-neutral-grey-100 enabled:focus-visible:border-3 enabled:focus-visible:border-solid enabled:focus-visible:border-icon-dark enabled:hover:focus-visible:bg-neutral-grey-200 enabled:active:bg-neutral-grey-200 ' +
                    (activeMode === 'list'
                      ? 'text-icon-dark active:text-disabled-dark'
                      : 'text-disabled-light')
                  }
                >
                  <TileViewSVG />
                </button>
                <button
                  type="button"
                  onClick={() => setActiveMode('list')}
                  disabled={activeMode === 'list'}
                  className={
                    'flex h-12 w-12 items-center justify-center rounded-full outline-none lg:enabled:hover:bg-neutral-grey-100 enabled:focus-visible:border-3 enabled:focus-visible:border-solid enabled:focus-visible:border-icon-dark enabled:hover:focus-visible:bg-neutral-grey-200 enabled:active:bg-neutral-grey-200 ' +
                    (activeMode === 'tile'
                      ? 'text-icon-dark active:text-disabled-dark'
                      : 'text-disabled-light')
                  }
                >
                  <ListViewSVG />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {((activeFilterCategory && activeFilterCategory.optionValue.length > 0) ||
        (activeFilterType && activeFilterType.optionValue.length > 0) ||
        (activeFilterString && activeFilterString.length > 0)) &&
      slicedSortedList.length === 0 ? (
        // <>Nohits</>
        <NoHits
          text={dictionary?.noMatches ?? ''}
          listType={listType}
          dictionary={dictionary}
          node_locale={node_locale}
          pageContext={pageContext}
          businessUnitCode={businessUnitCode}
          pageName={pageName}
        />
      ) : activeMode === 'list' ? (
        <ListView listArray={slicedSortedList} />
      ) : (
        <TileView
          dictionary={dictionary}
          listArray={slicedSortedList}
          tileColClass={tileColClass}
          attributeFilters={attributeFilters}
          listType={listType}
          node_locale={node_locale}
          // pageContext={pageContext}
          businessUnitCode={businessUnitCode}
          pageName={pageName}
          config={config ?? {}}
          ikeaFamilyBenefit={ikeaFamilyBenefit}
        />
      )}
      {cardList?.length > _pageSize - 1 && (
        <ShowMore
          currentPage={page}
          maxNum={filteredCardList.length}
          setPage={(newPage) => {
            setScrollPositionOnFilter(window.scrollY);
            setPage(newPage);
          }}
          pageSize={_pageSize}
          ofString={dictionary?.of}
          showMoreString={dictionary?.showMore}
          showingString={dictionary?.showing}
        />
      )}
      {!isMobileSearchFilterOpen && (
        // hide filter button for the editorial listing page — desktop only, mobile now uses inline button above
        <button
          className={`${
            listType === 'editorial' ? 'hidden' : ''
          } smartbanner-top-margin fixed right-6 top-6 z-10 hidden h-15 w-15 place-items-center rounded-full bg-brand-yellow-1 shadow-down-right outline-0 transition-all lg:hover:bg-accent-primary-dark focus:bg-accent-primary-dark focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-brand-grey-2 active:bg-accent-primary-dark lg:grid`}
          onClick={() => setIsMobileSearchFilterOpen(true)}
        >
          {activeFiltersCount > 0 && (
            <div className="absolute -right-1 -top-0.5 lg:hidden grid h-5 w-5 place-items-center rounded-full bg-brand-red-2 font-title text-buttonlabelxs text-brand-white">
              <span>{activeFiltersCount}</span>
            </div>
          )}
          <FilterIcon aria-label="Show filters" />
        </button>
      )}
      {isMobileSearchFilterOpen ? (
        // Backdrop is used for mobile size
        <Backdrop
          resetSearch={resetFilters}
          showButtons={false}
          handleOpen={handleOpenMobileSearchFilter}
          clearAllString={dictionary?.clearAll}
          showResultsString={dictionary?.showResults}
          className={`
          lg:hidden${
            isMobileSearchFilterOpen
              ? ''
              : ' translate-y-[calc(100%+10rem)] lg:hidden lg:translate-y-0'
          }
        `}
        >
          <InputField
            placeholder={`${dictionary?.find} ${pageName?.toLocaleLowerCase()}`}
            label=""
            disabled={false}
            type="text"
            name=""
            id=""
            value={activeFilterString ?? ''}
            onChange={(e) => setActiveFilterString(e.currentTarget.value)}
            icon="Search"
            className="relative z-40 mb-4 flex-1"
          />

          <FilterSidebar
            handleClose={() => {
              if (isMobileSearchFilterOpen) {
                setIsMobileSearchFilterOpen(false);
              } else {
                setOpenFilterPopup(false);
              }
            }}
            showAll={showAll}
            setShowAll={setShowAll}
            node_locale={node_locale}
            activeFilterValues={{
              category: activeFilterCategory.optionValue,
              cuisines: activeFilterType.optionValue,
              priceLevel: activeFilterPriceLevel.optionValue,
              tag: activeFilterTag.optionValue,
              additionalPerks: giftCard
                ? attributeFilters?.giftCardLabel
                : loyaltyCard
                  ? attributeFilters?.loyaltyCardLabel
                  : offerCard
                    ? attributeFilters?.offerPageLabel
                    : activeMemberDayCard
                      ? dictionary?.memberDayText
                      : '',
            }}
            filtersThatExist={{
              category:
                shouldShowSearchAndFilterPanel &&
                selectValueCategories?.length > 0,
              cuisines: shouldShowTypeFilter && selectValueTypes?.length > 0,
              priceLevel: shouldShowTypeFilter && priceLevelValues?.length > 0,
              tags: tagValues?.length > 0,
              additionalPerks:
                atLeastOneOfTheCardsHasEitherGiftcardOrLoyaltycard,
            }}
            categoryList={selectValueCategories}
            attributeFilterList={attributeFilters}
            cuisineList={selectValueTypes}
            onCategoryChange={setActiveFilterCategory}
            priceLevel={priceLevelValues}
            onCuisineChange={setActiveFilterType}
            filteredCardsCount={filteredCardList?.length}
            onPriceLevelChange={setActiveFilterPriceLevel}
            resetSearch={resetFilters}
            onGiftCardChange={handleGiftCardChange}
            onLoyaltyCardChange={handleLoyaltyCardChange}
            onOfferPageChange={handleOfferCardChange}
            onMemberDayCardChange={handleMemberDayCardChange}
            tagsList={tagValues}
            onTagChange={setActiveFilterTag}
          />

          <button
            className="absolute -top-20 right-4 z-40 grid h-12 w-12 place-items-center rounded-full bg-brand-grey-1 outline-0 transition-all focus:bg-brand-grey-2 focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-brand-grey-2 active:bg-brand-grey-2 lg:hidden lg:h-15 lg:w-15 lg:bg-brand-yellow-1 lg:hover:bg-accent-primary-dark lg:focus:bg-accent-primary-dark lg:active:bg-accent-primary-dark"
            onClick={() => setIsMobileSearchFilterOpen(false)}
          >
            <CloseIcon />
          </button>
        </Backdrop>
      ) : (
        <SideModal
          isOpen={openFilterPopup}
          handleClose={() => {
            if (isMobileSearchFilterOpen) {
              setIsMobileSearchFilterOpen(false);
            } else {
              setOpenFilterPopup(false);
            }
          }}
        >
          <SideModalBody>
            <FilterSidebar
              handleClose={() => {
                if (isMobileSearchFilterOpen) {
                  setIsMobileSearchFilterOpen(false);
                } else {
                  setOpenFilterPopup(false);
                }
              }}
              showAll={showAll}
              setShowAll={setShowAll}
              node_locale={node_locale}
              activeFilterValues={{
                category: activeFilterCategory.optionValue,
                cuisines: activeFilterType.optionValue,
                priceLevel: activeFilterPriceLevel.optionValue,
                tag: activeFilterTag.optionValue,
                additionalPerks: giftCard
                  ? attributeFilters?.giftCardLabel
                  : loyaltyCard
                    ? attributeFilters?.loyaltyCardLabel
                    : offerCard
                      ? attributeFilters?.offerPageLabel
                      : activeMemberDayCard
                        ? dictionary?.memberDayText
                        : '',
              }}
              filtersThatExist={{
                category:
                  shouldShowSearchAndFilterPanel &&
                  selectValueCategories?.length > 0,
                cuisines: shouldShowTypeFilter && selectValueTypes?.length > 0,
                priceLevel:
                  shouldShowTypeFilter && priceLevelValues?.length > 0,
                tags: tagValues?.length > 0,
                additionalPerks:
                  atLeastOneOfTheCardsHasEitherGiftcardOrLoyaltycard,
              }}
              categoryList={selectValueCategories}
              attributeFilterList={attributeFilters}
              cuisineList={selectValueTypes}
              onCategoryChange={setActiveFilterCategory}
              priceLevel={priceLevelValues}
              onCuisineChange={setActiveFilterType}
              filteredCardsCount={filteredCardList?.length}
              onPriceLevelChange={setActiveFilterPriceLevel}
              resetSearch={resetFilters}
              onGiftCardChange={handleGiftCardChange}
              onLoyaltyCardChange={handleLoyaltyCardChange}
              onOfferPageChange={handleOfferCardChange}
              onMemberDayCardChange={handleMemberDayCardChange}
              tagsList={tagValues}
              onTagChange={setActiveFilterTag}
            />
          </SideModalBody>
        </SideModal>
      )}
    </main>
  );
};

export default ListPage;

